import { m } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import { varHover } from 'src/components/animate';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import Scrollbar from 'src/components/scrollbar';
import { useAuthContext } from 'src/auth/hooks';
import { useNotificationContext } from 'src/hooks/use-notification-context';
import NotificationItem from './notification-item';
import { Notification } from 'src/models/notification';
import { RouterLink } from 'src/routes/components';
import { paths } from 'src/routes/paths';

const TABS = [
  { value: 'unread', label: 'Unread' },
  { value: 'all', label: 'All' },
  { value: 'archived', label: 'Archived' },
];

export default function NotificationsPopover() {
  const [currentTab, setCurrentTab] = useState('unread');
  const [notificationsList, setNotifications] = useState<Notification[]>([]);
  const { notifications, socket, editNotificationHandler } = useNotificationContext();

  const [tabCounts, setTabCounts] = useState({ unread: 0, all: 0, archived: 0 });

  const { user } = useAuthContext();
  const currentUserId = user?._id;

  useEffect(() => {
    if (socket) {
      socket.emit('someEvent', { data: 'exampleData' });
    }
  }, [socket]);

  const drawer = useBoolean();
  const smUp = useResponsive('up', 'sm');

  const isMine = useCallback(
    (n: Notification) =>
      n.recipients.some(
        (r) =>
          r.recipientId === currentUserId ||
          (user?.jobRoles && user.jobRoles.includes(r.recipientId))
      ),
    [currentUserId, user?.jobRoles]
  );

  const isUnreadForMe = useCallback(
    (n: Notification) =>
      n.recipients.some(
        (r) =>
          (r.recipientId === currentUserId ||
            (user?.jobRoles && user.jobRoles.includes(r.recipientId))) &&
          !r.isRead
      ),
    [currentUserId, user?.jobRoles]
  );

  const isArchivedForMe = useCallback(
    (n: Notification) =>
      n.recipients.some(
        (r) =>
          (r.recipientId === currentUserId ||
            (user?.jobRoles && user.jobRoles.includes(r.recipientId))) &&
          r.isArchived
      ),
    [currentUserId, user?.jobRoles]
  );

  // Merge the context's notifications into the popover's local list. Context
  // is the source of truth for existing IDs — so an update elsewhere (e.g.
  // "mark all as read" on the notification page) propagates here instead of
  // being ignored because the id already exists locally.
  useEffect(() => {
    if (!notifications?.length) return;
    setNotifications((prev) => {
      const merged = new Map<string, Notification>();
      prev.forEach((n) => merged.set(n._id, n));
      notifications.forEach((n) => merged.set(n._id, n));
      return Array.from(merged.values());
    });
  }, [notifications]);

  useEffect(() => {
    const mine = notificationsList.filter(isMine).filter((n) => !isArchivedForMe(n));
    const archivedMine = notificationsList.filter(isMine).filter(isArchivedForMe);
    setTabCounts({
      unread: mine.filter(isUnreadForMe).length,
      all: mine.length,
      archived: archivedMine.length,
    });
  }, [notificationsList, isMine, isUnreadForMe, isArchivedForMe]);

  const totalUnRead = tabCounts.unread;

  const handleMarkAllAsRead = async () => {
    const unread = notificationsList.filter(isUnreadForMe);
    if (!unread.length) return;

    const recipientsToUpdate = unread.flatMap((n) =>
      n.recipients
        .filter(
          (r) =>
            (r.recipientId === currentUserId ||
              (user?.jobRoles && user.jobRoles.includes(r.recipientId))) &&
            !r.isRead
        )
        .map((r) => ({ recipientId: r.recipientId, isRead: true }))
    );

    await editNotificationHandler({
      notificationIds: unread.map((n) => n._id),
      recipients: recipientsToUpdate,
      status: 'Read',
    });

    setNotifications((prev) =>
      prev.map((n) => ({
        ...n,
        recipients: n.recipients.map((r) =>
          r.recipientId === currentUserId ||
          (user?.jobRoles && user.jobRoles.includes(r.recipientId))
            ? { ...r, isRead: true }
            : r
        ),
      }))
    );
  };

  const handleMarkAsRead = async (notificationId: string) => {
    const notification = notificationsList.find((n) => n._id === notificationId);
    if (!notification) return;

    const recipientsToUpdate = notification.recipients
      .filter(
        (r) =>
          (r.recipientId === currentUserId ||
            (user?.jobRoles && user.jobRoles.includes(r.recipientId))) &&
          !r.isRead
      )
      .map((r) => ({ recipientId: r.recipientId, isRead: true }));

    if (!recipientsToUpdate.length) return;

    await editNotificationHandler({
      notificationIds: [notificationId],
      recipients: recipientsToUpdate,
      status: 'Read',
    });

    setNotifications((prev) =>
      prev.map((n) =>
        n._id === notificationId
          ? {
              ...n,
              recipients: n.recipients.map((r) =>
                r.recipientId === currentUserId ||
                (user?.jobRoles && user.jobRoles.includes(r.recipientId))
                  ? { ...r, isRead: true }
                  : r
              ),
            }
          : n
      )
    );
  };

  // const handleClearAll = async () => {
  //   await clearAllNotifications()
  // };

  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  const renderHead = (
    <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!!totalUnRead && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      {/* Clear All button
      {tabCounts.all > 0 && (
        <Tooltip title="Clear all">
          <IconButton color="default" onClick={handleClearAll}>
            <Iconify icon="solar:trash-bin-trash-bold" />
          </IconButton>
        </Tooltip>
      )} */}

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderTabs = (
    <Tabs value={currentTab} onChange={handleChangeTab}>
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={((tab.value === 'all' || tab.value === currentTab) && 'filled') || 'soft'}
              color={
                (tab.value === 'unread' && 'info') ||
                (tab.value === 'archived' && 'success') ||
                'default'
              }
            >
              {tabCounts[tab.value as keyof typeof tabCounts]}
            </Label>
          }
          sx={{ '&:not(:last-of-type)': { mr: 3 } }}
        />
      ))}
    </Tabs>
  );

  const renderList = (
    <Scrollbar>
      <List disablePadding>
        {notificationsList
          .filter(isMine)
          .filter((n) => {
            if (currentTab === 'archived') return isArchivedForMe(n);
            if (currentTab === 'unread') return isUnreadForMe(n) && !isArchivedForMe(n);
            return !isArchivedForMe(n);
          })
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .map((n) => {
            const currentRecipient =
              n.recipients.find((r) => r.recipientId === currentUserId) ||
              n.recipients.find((r) => user?.jobRoles?.includes(r.recipientId));

            return (
              <NotificationItem
                key={n._id}
                notification={n}
                currentRecipient={currentRecipient!}
                handleMarkAsRead={handleMarkAsRead}
              />
            );
          })}
      </List>
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? 'primary' : 'default'}
        onClick={drawer.onTrue}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{ sx: { width: 1, maxWidth: 420 } }}
      >
        {renderHead}
        <Divider />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pl: 2.5, pr: 1 }}
        >
          {renderTabs}
        </Stack>
        <Divider />
        {renderList}
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            size="large"
            component={RouterLink}
            href={paths.notifications.root}
            onClick={drawer.onFalse}
          >
            View All
          </Button>
        </Box>
      </Drawer>
    </>
  );
}